// VERA-246: icon picker for the "Custom" address type. A searchable grid of the
// curated lucide set (utils/addressIcons). The selected icon's stable key is
// stored on address.icon and rendered everywhere via getAddressIcon.

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Box, Stack, Typography, Tooltip, useTheme } from '@mui/material'
import { SearchIcon } from 'lucide-react'
import { Input, useScrollFade } from '@repo/ui'

import { ADDRESS_ICON_OPTIONS } from 'utils/addressIcons'

interface AddressIconPickerProps {
  value: string | null
  onChange: (key: string) => void
}

export const AddressIconPicker = ({ value, onChange }: AddressIconPickerProps) => {
  const theme = useTheme()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return ADDRESS_ICON_OPTIONS
    return ADDRESS_ICON_OPTIONS.filter(o => o.label.toLowerCase().includes(q))
  }, [query])

  // Scroll-aware bottom fade — shared hook (@repo/ui useScrollFade). It watches
  // children as well as the container, so narrowing the grid via search
  // re-evaluates without this component tracking `filtered` itself.
  const { ref: scrollRef, fadeSx } = useScrollFade({ fadeHeight: 24 })

  return (
    <Stack spacing={2}>
      <Input
        placeholder="Search icons"
        value={query}
        onValueChange={setQuery}
        startAdornment={<SearchIcon size={16} strokeWidth={1} />}
      />

      <Box
        ref={scrollRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 2,
          maxHeight: 168,
          overflowY: 'auto',
          p: 1,
          // Keep the scrollbar from crowding the last column.
          pr: 2,
          // Soft fade at the bottom edge to hint there are more icons below — but
          // only while there's more to scroll. Once scrolled to the end (or the
          // grid doesn't overflow) the mask is dropped so the last row is crisp.
          ...fadeSx,
        }}
      >
        {filtered.map(({ key, label, Icon }) => {
          const selected = key === value
          return (
            <Tooltip key={key} title={label} placement="top">
            <Box
              role="button"
              aria-label={label}
              aria-pressed={selected}
              tabIndex={0}
              onClick={() => onChange(key)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onChange(key)
                }
              }}
              sx={{
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 2,
                border: `1px solid ${
                  selected ? theme.palette.brand[600] : theme.palette.primary[100]
                }`,
                backgroundColor: selected ? theme.palette.brand['600-6%'] : 'transparent',
                color: selected ? theme.palette.brand[600] : theme.palette.text.secondary,
                transition: 'all 120ms ease',
                '&:hover': {
                  borderColor: theme.palette.brand[600],
                  backgroundColor: theme.palette.brand['600-6%'],
                },
              }}
            >
              <Icon size={20} strokeWidth={1} />
            </Box>
            </Tooltip>
          )
        })}
      </Box>

      {filtered.length === 0 && (
        <Typography variant="body4" color="text.secondary">
          No icons match &ldquo;{query}&rdquo;.
        </Typography>
      )}
    </Stack>
  )
}
